import { AnimatePresence, motion } from 'motion/react';
import type { GameState } from '../../../../modules/game/LootBoxOpening';
import { Loader, Button, useBodyScrollLock } from '../../../../modules/design-system';
import { useAddFundsAction } from '../../../../modules/operatorLinks';
import { useBackBlocker, useBackHandler } from '../../../../modules/backStack';

export interface PurchaseOverlayProps {
  state: GameState;
  onClose: () => void;
}

/**
 * Full-page overlay shown during the buy round-trip and on failure.
 * Success path closes the overlay and hands off to <RevealScreen> via
 * the host's `onRevealReady` URL update — so there's nothing to render
 * here past `'purchasing'`.
 */
export function PurchaseOverlay({ state, onClose }: PurchaseOverlayProps) {
  const visible = state.step !== 'idle';
  // This overlay stays mounted while hidden, so the lock follows `visible`.
  useBodyScrollLock(visible);
  // Host back button (modules/backStack): swallowed while the purchase is in
  // flight, dismisses the failure screen like its own button.
  useBackBlocker(state.step === 'purchasing');
  useBackHandler(state.step === 'failed', onClose, 'overlay');

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="purchase-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="
            safe-area-pad fixed inset-0 z-50 flex items-center justify-center
            bg-black/70 backdrop-blur-md
            px-4
          "
          role="dialog"
          aria-modal="true"
        >
          <PhaseContent state={state} onClose={onClose} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function PhaseContent({ state, onClose }: PurchaseOverlayProps) {
  if (state.step === 'purchasing') {
    return <PurchasingSpinner />;
  }
  if (state.step === 'failed') {
    return <FailureScreen message={state.message} code={state.code} onClose={onClose} />;
  }
  return null;
}

function PurchasingSpinner() {
  return (
    // text-white paints the caption AND the loader, which is currentColor.
    // Deliberately not the operator accent: a red-branded operator made the
    // loader read as an error state on this dim backdrop.
    <div className="flex flex-col items-center gap-4 text-white">
      <Loader />
      <p className="type-headline font-semibold">Getting ready to rip...</p>
    </div>
  );
}

function FailureScreen({
  message,
  code,
  onClose,
}: {
  message: string;
  code?: string | undefined;
  onClose: () => void;
}) {
  // Insufficient balance + casino deposit_url → "Add funds" closes this and
  // opens the cashier; the player comes back and rips again. Otherwise unchanged.
  const onAddFunds = useAddFundsAction(code, onClose);
  return (
    <motion.div
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.25 }}
      className="
        w-full max-w-[440px] rounded-[28px] p-6 text-white
        border border-[rgba(171,174,179,0.24)]
        bg-[linear-gradient(180deg,rgba(255,255,255,0.08)_0%,rgba(255,255,255,0.02)_100%)]
        backdrop-blur-[20px]
      "
    >
      <p className="type-subhead opacity-80 mb-6 break-words">{message}</p>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-full px-5 py-2.5 type-body-semi border border-white/30"
        >
          Close
        </button>
        {onAddFunds && (
          <Button variant="primary" size="lg" onClick={onAddFunds} className="rounded-full">
            Add funds
          </Button>
        )}
      </div>
    </motion.div>
  );
}
